/**
 * ClinSpaceXR entry point: the entry portal hands over to App, which runs the lobby and
 * the study rooms. Everything inside XR is world-space; this file only touches the DOM.
 */
import '@fontsource-variable/sora';
import '@fontsource-variable/inter';
import { ROLE_STYLES } from '@clinspace/shared';
import { App } from './app/App.ts';
import { CONTROLS } from './app/controls.ts';
import { installDebug } from './app/debug.ts';
import { deviceSignals, isHandheld } from './app/device.ts';
import { startPortalBackground } from './app/portalBackground.ts';
import { isValidName, saveProfile } from './app/profile.ts';
import { page } from './lib/api.ts';
import { loadItem, saveItem } from './lib/storage.ts';

const canvas = document.getElementById('scene') as HTMLCanvasElement;
const landing = document.getElementById('landing') as HTMLDivElement;
const enterVrButton = document.getElementById('enter-vr') as HTMLButtonElement;
const enterDesktopButton = document.getElementById('enter-desktop') as HTMLButtonElement;
const hint = document.getElementById('landing-hint') as HTMLParagraphElement;
const hud = document.getElementById('hud') as HTMLDivElement;
const hudFps = document.getElementById('hud-fps') as HTMLSpanElement;
const hudMode = document.getElementById('hud-mode') as HTMLSpanElement;
const hudStats = document.getElementById('hud-stats') as HTMLSpanElement;
const crosshair = document.getElementById('crosshair') as HTMLDivElement;
const greeting = document.getElementById('greeting') as HTMLDivElement;
const greetingName = document.getElementById('greeting-name') as HTMLElement;
const greetingRole = document.getElementById('greeting-role') as HTMLSpanElement;
const greetingDot = document.getElementById('greeting-dot') as HTMLSpanElement;
const notYou = document.getElementById('not-you') as HTMLButtonElement;
const actions = document.querySelector('.portal-card .actions') as HTMLDivElement;
const statusList = document.querySelector('.portal-card .status') as HTMLUListElement;
const deviceNote = document.getElementById('device-note') as HTMLElement;

(document.getElementById('link-queries') as HTMLAnchorElement).href = page('queries');

/**
 * Phones and tablets (2026-10-06): the lobby and rooms need a headset or a mouse and keyboard,
 * so a handheld visitor is told so, with the link to open elsewhere. "Continue anyway" is
 * there for testing and for devices the check gets wrong.
 */
function showDeviceNote(): void {
  deviceNote.hidden = false;
  actions.hidden = true;
  statusList.hidden = true;
  const url = new URL(page('app'), location.origin).href;
  const shown = document.getElementById('device-url') as HTMLSpanElement;
  shown.textContent = url;
  const copy = document.getElementById('copy-link') as HTMLButtonElement;
  copy.addEventListener('click', () => {
    navigator.clipboard
      .writeText(url)
      .then(() => (copy.textContent = 'Copied'))
      .catch(() => {
        // No clipboard here: select the address so it can be copied by hand.
        const range = document.createRange();
        range.selectNodeContents(shown);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
        copy.textContent = 'Select and copy';
      });
  });
  (document.getElementById('continue-anyway') as HTMLButtonElement).addEventListener(
    'click',
    () => {
      deviceNote.hidden = true;
      actions.hidden = false;
      statusList.hidden = false;
    },
  );
}
if (isHandheld(deviceSignals())) showDeviceNote();

startPortalBackground(
  document.getElementById('portal-bg') as HTMLCanvasElement,
  () => !landing.hidden,
);

type StatusState = 'pending' | 'ok' | 'info' | 'error';
function setStatus(id: 'server' | 'headset', state: StatusState, text: string): void {
  const li = document.getElementById(`status-${id}`) as HTMLLIElement;
  li.dataset.state = state;
  (li.querySelector('.status-text') as HTMLSpanElement).textContent = text;
}

const app = new App(canvas, {
  onSessionEnd: () => {
    landing.hidden = false;
    landing.classList.remove('fade');
    hud.hidden = true;
    hint.textContent = 'You left VR. Enter again or continue on desktop.';
  },
  onStats: (s) => {
    hudFps.textContent = `${s.fps.toFixed(0)} fps`;
    hudStats.textContent = `${s.triangles.toLocaleString()} tris · ${s.drawCalls} calls · ${s.scene}`;
  },
});

if (import.meta.env.DEV) installDebug(app);

/** A returning visitor is greeted by name and role; "Not you?" starts over. */
function showGreeting(): void {
  const p = app.profile;
  if (!p.onboarded || !isValidName(p.name)) {
    greeting.hidden = true;
    return;
  }
  greetingName.textContent = p.name;
  greetingRole.textContent = p.role;
  greetingDot.style.color = ROLE_STYLES[p.role].hex;
  greetingDot.style.background = ROLE_STYLES[p.role].hex;
  greeting.hidden = false;
}
showGreeting();
notYou.addEventListener('click', () => {
  app.profile.name = '';
  app.profile.onboarded = false;
  saveProfile(app.profile);
  showGreeting();
  hint.textContent = 'You will choose a name and role in the lobby.';
});

function hideLanding(mode: 'vr' | 'desktop'): void {
  landing.classList.add('fade');
  // Leaving VR during the fade brings the portal back (onSessionEnd drops the fade); it
  // must stay, or the visitor is left on a bare page with no way back into VR.
  setTimeout(() => {
    if (landing.classList.contains('fade')) landing.hidden = true;
  }, 700);
  hud.hidden = false;
  hudMode.textContent = mode === 'vr' ? 'VR' : 'Desktop';
  saveItem('lastMode', mode);
}

enterDesktopButton.disabled = true;

/** Retries until the server answers, so a restarted or slow server never strands the portal. */
async function connect(): Promise<void> {
  for (let attempt = 1; ; attempt++) {
    try {
      await app.load();
      break;
    } catch (err) {
      const wait = Math.min(2000 * attempt, 10_000);
      setStatus('server', 'error', 'Could not reach the ClinSpaceXR server · retrying');
      setStatus('headset', 'pending', 'Waiting for the server');
      hint.textContent = `${(err as Error).message}. Trying again in ${wait / 1000} s.`;
      await new Promise((r) => setTimeout(r, wait));
      setStatus('server', 'pending', 'Connecting to the institute…');
    }
  }
  hint.textContent = '';
  const n = app.manifest?.studies.length ?? 0;
  setStatus('server', 'ok', `Connected · ${n} ${n === 1 ? 'study' : 'studies'} available`);
  if (await app.xrSupported()) {
    // A headset that looks like a tablet (Apple Vision Pro) was shown the handheld note.
    if (!deviceNote.hidden && !isHandheld({ ...deviceSignals(), immersiveVr: true })) {
      deviceNote.hidden = true;
      actions.hidden = false;
      statusList.hidden = false;
    }
    enterVrButton.hidden = false;
    setStatus('headset', 'info', 'Headset detected');
  } else {
    setStatus('headset', 'info', 'No headset detected · mouse and keyboard');
  }
  enterDesktopButton.disabled = false;
}
const ready = connect();

enterVrButton.addEventListener('click', async () => {
  await ready;
  try {
    await app.start('vr');
    // VR may already have ended while the lobby loaded (headset taken off, a system
    // dialog): then the portal stays, with Enter in VR.
    if (app.presenting) hideLanding('vr');
  } catch (err) {
    // Answering the microphone prompt can take longer than the browser allows between the
    // click and starting VR (or VR permission was declined); a second press works, since
    // the microphone is settled by then.
    const name = (err as DOMException).name;
    hint.textContent =
      name === 'NotAllowedError' || name === 'SecurityError'
        ? 'VR did not start. Press Enter in VR again.'
        : `Could not start VR: ${(err as Error).message}`;
  }
});

enterDesktopButton.addEventListener('click', async () => {
  await ready;
  hideLanding('desktop');
  await app.start('desktop');
});

document.addEventListener('pointerlockchange', () => {
  crosshair.hidden = document.pointerLockElement !== canvas;
});

/* ---------- Every desktop control (2026-10-06): ? or the HUD button ---------- */

const controlsPanel = document.getElementById('controls') as HTMLElement;
const controlsGroups = document.getElementById('controls-groups') as HTMLDivElement;
const controlsWhere = document.getElementById('controls-where') as HTMLParagraphElement;

const escapeHtml = (s: string) =>
  s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);

function renderControls(): void {
  const here = app.roomDebug() ? 'room' : 'lobby';
  controlsWhere.textContent =
    here === 'room'
      ? 'You are in a study room: everything below works here.'
      : 'You are in the lobby: the dimmed groups work once you are in a study room.';
  // Three columns balanced by rows, each group whole, read top to bottom then across (CSS
  // columns in a box of fixed height spill sideways out of view instead of scrolling).
  const columns: string[][] = [[], [], []];
  const height = [0, 0, 0];
  for (const g of CONTROLS) {
    const elsewhere = g.where && g.where !== here;
    const tag = g.where ? `<small>${g.where === 'room' ? 'study rooms' : 'lobby'}</small>` : '';
    const items = g.controls
      .map(
        (c) =>
          `<dt>${c.keys.map((k) => `<kbd class="cap">${escapeHtml(k)}</kbd>`).join('')}</dt><dd>${escapeHtml(c.what)}</dd>`,
      )
      .join('');
    const col = height.indexOf(Math.min(...height));
    columns[col]!.push(
      `<section class="controls-group${elsewhere ? ' elsewhere' : ''}"><h3>${escapeHtml(g.title)}${tag}</h3><dl>${items}</dl></section>`,
    );
    height[col]! += g.controls.length + 2;
  }
  controlsGroups.innerHTML = columns
    .map((c) => `<div class="controls-column">${c.join('')}</div>`)
    .join('');
}

function showControls(open: boolean): void {
  if (open) {
    renderControls();
    // The list may need scrolling and the Close button clicking: free the mouse.
    if (document.pointerLockElement) document.exitPointerLock();
  }
  // While the list is open the app's own shortcuts and walking pause, as for a text field:
  // in a room, Esc closing the list would otherwise also leave the room.
  app.input.overlay = open;
  controlsPanel.hidden = !open;
}

document.getElementById('hud-controls')!.addEventListener('click', () => showControls(true));
document.getElementById('controls-close')!.addEventListener('click', () => showControls(false));
window.addEventListener('keydown', (e) => {
  if (!landing.hidden || app.presenting) return;
  if (controlsPanel.hidden && app.input.captureText) return;
  if (e.key === '?') {
    e.preventDefault();
    showControls(controlsPanel.hidden);
  } else if (e.key === 'Escape' && !controlsPanel.hidden) showControls(false);
});

// Enter continues on desktop from the portal (for returning desktop visitors it is the
// expected path; the button says so).
window.addEventListener('keydown', (e) => {
  if (e.key === 'Enter' && !landing.hidden && !actions.hidden && !enterDesktopButton.disabled) {
    if (document.activeElement instanceof HTMLButtonElement) return;
    enterDesktopButton.click();
  }
});
if (loadItem<string | undefined>('lastMode', undefined) === 'desktop') enterDesktopButton.focus();
